import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModuleHeader, { ModuleHeaderAction } from "../../components/ModuleHeader/ModuleHeader";
import ModuleTable from "../../components/ModuleTable/ModuleTable";
import { listMerchantOwners } from "../../api/merchantOwnerApi";
import "./merchant-owner-access.scss";

const OWNER_COLUMNS = ["Owner", "Login Phone Number", "Merchant Access", "Status", "Last Login", "Actions"];
const OWNER_TEMPLATE = "minmax(130px, 1fr) minmax(155px, 1.15fr) minmax(190px, 1.5fr) minmax(90px, .7fr) minmax(125px, .9fr) minmax(75px, .65fr)";

const formatLastLogin = (value) => {
  if (!value) return "Never";
  return new Intl.DateTimeFormat("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
};

const MerchantOwnerAccessPage = () => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const result = await listMerchantOwners({ keyword, status });
        setOwners(result.data || []);
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    }, keyword ? 300 : 0);

    return () => clearTimeout(timer);
  }, [keyword, status]);

  const merchantAccessLabel = (merchants = []) => {
    const names = merchants.map((merchant) => merchant.name);
    if (names.length <= 2) return names.join(", ") || "No access";
    return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
  };

  const emptyMessage = error
    || (loading ? "Loading merchant owners…" : null)
    || (!owners.length ? "No Data Available" : null);

  return (
    <main className="merchant-owner-access">
      <ModuleHeader
        bleed
        title="Merchant Owners"
        description="Manage platform-level owner accounts and assign access to one or more merchants."
        actions={(
          <ModuleHeaderAction type="button" onClick={() => navigate("/merchant-owners/create")}>
            + Add Merchant Owner
          </ModuleHeaderAction>
        )}
      />

      <section className="merchant-owner-access__filters">
        <label>
          <span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </label>
        <label>
          <span>Keyword</span>
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="Search owner, phone, or merchant..."
          />
        </label>
      </section>

      <ModuleTable
        spaced={false}
        title="Merchant Owner Access"
        description="Phone numbers are unique system-wide. Each owner may access multiple merchants and all outlets under them."
        count={`${owners.length} Owners`}
        columns={OWNER_COLUMNS}
        columnTemplate={OWNER_TEMPLATE}
        variant="merchant-owners"
        empty={emptyMessage}
      >
        {!loading && !error && owners.map((owner) => {
          const statusLabel = Number(owner.status) === 1 ? "Active" : "Inactive";
          return (
            <ModuleTable.Row key={owner.id} columnTemplate={OWNER_TEMPLATE}>
              <strong>{owner.name}</strong>
              <span>+{owner.phone}</span>
              <span>{merchantAccessLabel(owner.merchants)}</span>
              <span>
                <ModuleTable.Status value={statusLabel}>{statusLabel}</ModuleTable.Status>
              </span>
              <span>{formatLastLogin(owner.last_logged_in_at)}</span>
              <span>
                <ModuleTable.Action
                  type="button"
                  onClick={() => navigate(`/merchant-owners/${owner.id}/edit`)}
                >
                  Manage
                </ModuleTable.Action>
              </span>
            </ModuleTable.Row>
          );
        })}
      </ModuleTable>
    </main>
  );
};

export default MerchantOwnerAccessPage;
